import { useEffect, useState } from "react";

import DrawerHeader from "./DrawerHeader";
import DrawerNavigation from "./DrawerNavigation";

import ProjectInformation from "../overview/ProjectInformation";
import Members from "../members/Members";
import Workspaces from "../workspaces/Workspaces";

import { getEmployees } from "../services/employeeService";
import {
  getWorkspaces,
  getWorkspaceItems,
} from "../services/workspaceService";

import { getProjectTaskProgress } from "../calculations/projectTaskCalculations";

const ProjectDrawer = ({
  open,
  onClose,
  project,
}) => {
  const [activeTab, setActiveTab] = useState("Overview");

  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);

  const [projectItems, setProjectItems] = useState([]);
  const [progress, setProgress] = useState(0);
  const [progressLoading, setProgressLoading] = useState(false);

  // ============================================================
  // GET PROJECT UUID
  // ============================================================

  const projectUuid =
    project?.uuid ||
    project?.project_uuid ||
    project?.projectUuid;

  // ============================================================
  // FETCH EMPLOYEES
  // ============================================================

  useEffect(() => {
    if (!open) return;

    const fetchEmployees = async () => {
      try {
        setEmployeesLoading(true);

        const response = await getEmployees();

        console.log(
          "PROJECT DRAWER EMPLOYEES:",
          response
        );

        const employeeList = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.data)
          ? response.data.data
          : [];

        setEmployees(employeeList);
      } catch (error) {
        console.error(
          "FAILED TO FETCH EMPLOYEES:",
          error
        );

        setEmployees([]);
      } finally {
        setEmployeesLoading(false);
      }
    };

    fetchEmployees();
  }, [open]);

  // ============================================================
  // FETCH ALL PROJECT TASKS
  // ============================================================

  useEffect(() => {
    if (!open || !projectUuid) {
      setProjectItems([]);
      setProgress(0);
      return;
    }

    const fetchProjectTasks = async () => {
      try {
        setProgressLoading(true);

        // --------------------------------------------
        // GET PROJECT WORKSPACES
        // --------------------------------------------

        const workspaceResponse =
          await getWorkspaces(projectUuid);

        const workspaces = Array.isArray(workspaceResponse)
          ? workspaceResponse
          : Array.isArray(workspaceResponse?.data)
          ? workspaceResponse.data
          : Array.isArray(workspaceResponse?.data?.data)
          ? workspaceResponse.data.data
          : [];

        console.log(
          "PROJECT WORKSPACES FOR PROGRESS:",
          workspaces
        );

        // --------------------------------------------
        // GET ITEMS FROM ALL WORKSPACES
        // --------------------------------------------

        const allItems = [];

        for (const workspace of workspaces) {
          const workspaceUuid =
            workspace?.uuid ||
            workspace?.workspace_uuid ||
            workspace?.id;

          if (!workspaceUuid) continue;

          try {
            const itemResponse =
              await getWorkspaceItems(workspaceUuid);

            const items = Array.isArray(itemResponse)
              ? itemResponse
              : Array.isArray(itemResponse?.data)
              ? itemResponse.data
              : Array.isArray(itemResponse?.data?.data)
              ? itemResponse.data.data
              : [];

            allItems.push(...items);
          } catch (error) {
            console.error(
              `FAILED TO GET ITEMS FOR WORKSPACE ${workspaceUuid}`,
              error
            );
          }
        }

        console.log(
          "ALL PROJECT TASKS:",
          allItems
        );

        // --------------------------------------------
        // CALCULATE PROGRESS
        // --------------------------------------------

        const calculatedProgress =
          getProjectTaskProgress(allItems);

        console.log(
          "PROJECT PROGRESS:",
          calculatedProgress + "%"
        );

        setProjectItems(allItems);
        setProgress(calculatedProgress);
      } catch (error) {
        console.error(
          "FAILED TO CALCULATE PROJECT PROGRESS:",
          error
        );

        setProjectItems([]);
        setProgress(0);
      } finally {
        setProgressLoading(false);
      }
    };

    fetchProjectTasks();
  }, [open, projectUuid]);

  // ============================================================
  // RESET TAB WHEN PROJECT CHANGES
  // ============================================================

  useEffect(() => {
    if (open) {
      setActiveTab("Overview");
    }
  }, [open, projectUuid]);

  // ============================================================
  // CLOSE DRAWER
  // ============================================================

  if (!open || !project) return null;

  return (
    <>
      {/* ======================================================
          BACKDROP
      ====================================================== */}

      <div
        className="fixed inset-0 z-[50] bg-black/40"
        onClick={onClose}
      />

      {/* ======================================================
          DRAWER
      ====================================================== */}

      <div className="fixed right-0 top-0 z-[60] flex h-screen w-full flex-col border-l border-slate-200 bg-white shadow-2xl md:w-[80%] lg:w-[70%] xl:w-1/2">

        {/* ====================================================
            HEADER
        ==================================================== */}

        <DrawerHeader
          project={project}
          employees={employees}
          employeesLoading={employeesLoading}
          progress={progress}
          progressLoading={progressLoading}
          onClose={onClose}
        />

        {/* ====================================================
            NAVIGATION
        ==================================================== */}

        <DrawerNavigation
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />

        {/* ====================================================
            CONTENT
        ==================================================== */}

        <div className="flex-1 overflow-y-auto bg-slate-50 p-6">

          {activeTab === "Overview" && (
            <ProjectInformation
              project={project}
              employees={employees}
              employeesLoading={employeesLoading}
              projectItems={projectItems}
              progress={progress}
              progressLoading={progressLoading}
            />
          )}

          {activeTab === "Members" && (
            <Members
              project={project}
            />
          )}

          {activeTab === "Workspaces" && (
            <Workspaces
              project={project}
            />
          )}

        </div>
      </div>
    </>
  );
};

export default ProjectDrawer;